import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from './api.types';

interface RequestOptions {
    headers?: HttpHeaders | { [header: string]: string | string[] };
    params?: HttpParams | { [param: string]: string | string[] };
}

/**
 * Servicio central para todas las peticiones HTTP al API
 * Maneja la URL base desde environment y configura automáticamente credentials
 */
@Injectable({
    providedIn: 'root',
})
export class ApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = environment.api;

    /**
     * Realiza una petición GET
     */
    get<T>(endpoint: string, options?: RequestOptions): Observable<ApiResponse<T>> {
        return this.http
            .get<ApiResponse<T>>(this.buildUrl(endpoint), {
                ...options,
                withCredentials: true,
            })
            .pipe(catchError((error) => this.handleError<T>(error)));
    }

    /**
     * Realiza una petición POST
     */
    post<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Observable<ApiResponse<T>> {
        return this.http
            .post<ApiResponse<T>>(this.buildUrl(endpoint), body, {
                ...options,
                withCredentials: true,
            })
            .pipe(catchError((error) => this.handleError<T>(error)));
    }

    /**
     * Realiza una petición PUT
     */
    put<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Observable<ApiResponse<T>> {
        return this.http
            .put<ApiResponse<T>>(this.buildUrl(endpoint), body, {
                ...options,
                withCredentials: true,
            })
            .pipe(catchError((error) => this.handleError<T>(error)));
    }

    /**
     * Realiza una petición PATCH
     */
    patch<T>(endpoint: string, body: unknown = {}, options?: RequestOptions): Observable<ApiResponse<T>> {
        return this.http
            .patch<ApiResponse<T>>(this.buildUrl(endpoint), body, {
                ...options,
                withCredentials: true,
            })
            .pipe(catchError((error) => this.handleError<T>(error)));
    }

    /**
     * Realiza una petición DELETE
     */
    delete<T>(endpoint: string, options?: RequestOptions): Observable<ApiResponse<T>> {
        return this.http
            .delete<ApiResponse<T>>(this.buildUrl(endpoint), {
                ...options,
                withCredentials: true,
            })
            .pipe(catchError((error) => this.handleError<T>(error)));
    }

    /**
     * Construye la URL completa a partir del endpoint
     */
    private buildUrl(endpoint: string): string {
        // Elimina slash inicial si existe para evitar doble slash
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        return `${this.baseUrl}/${cleanEndpoint}`;
    }

    /**
     * Maneja errores de las peticiones HTTP
     */
    private handleError<T>(error: HttpErrorResponse): Observable<ApiResponse<T>> {
        console.error('API Error:', error);

        // Si el error tiene respuesta del servidor, la usamos
        if (error.error && typeof error.error === 'object' && 'success' in error.error) {
            return of(error.error as ApiResponse<T>);
        }

        // Error genérico
        return of({
            data: null as unknown as T,
            success: false,
            msg: error.message || 'Error de conexión',
            code: 'api/network-error',
        });
    }
}
